import React, { useState, useMemo } from 'react';
import { BarChart3, Download, User, TrendingUp, MessageSquare, ChevronDown, ChevronUp } from 'lucide-react';
import api from '../../../utils/api';
import toast from 'react-hot-toast';

const AggregatedView = ({ instances, survey, surveyId }) => {
    const [expandedQuestions, setExpandedQuestions] = useState(new Set([0])); // First question expanded by default
    const [exporting, setExporting] = useState(false);

    // Aggregate responses by question
    const aggregatedData = useMemo(() => {
        if (!survey?.questions || !instances) return [];

        return survey.questions.map((question, qIndex) => {
            const responses = [];
            
            instances.forEach((instance) => {
                if (instance.responses && instance.responses.length > 0) {
                    const response = instance.responses.find(
                        r => r.question_id === question.id || 
                             r.snapshot_question_text === question.question_text
                    ) || instance.responses[qIndex]; // Fallback to index matching

                    if (response) {
                        responses.push({
                            employeeName: instance.employee_name || `Employee #${instance.employee_id}`,
                            employeeEmail: instance.employee_email,
                            completedAt: instance.completed_at,
                            responseText: response.response_text || response.response_value,
                            skipped: response.skipped,
                            sentiment: response.sentiment_label
                        });
                    }
                }
            });

            // Calculate sentiment distribution
            const sentimentCounts = {
                Positive: responses.filter(r => r.sentiment === 'Positive').length,
                Neutral: responses.filter(r => r.sentiment === 'Neutral').length,
                Negative: responses.filter(r => r.sentiment === 'Negative').length
            };

            return {
                question,
                responses,
                sentimentCounts,
                totalResponses: responses.length,
                skippedCount: responses.filter(r => r.skipped).length
            };
        });
    }, [survey, instances]);

    const toggleQuestion = (index) => {
        const newExpanded = new Set(expandedQuestions);
        if (newExpanded.has(index)) {
            newExpanded.delete(index);
        } else {
            newExpanded.add(index);
        }
        setExpandedQuestions(newExpanded);
    };

    const handleExportQuestion = async (questionIndex, format = 'csv') => {
        try {
            setExporting(true);
            const questionData = aggregatedData[questionIndex];
            
            const response = await api.post(`/surveys/${surveyId}/export-question`, {
                questionId: questionData.question.id,
                questionText: questionData.question.question_text,
                responses: questionData.responses,
                format
            }, {
                responseType: 'blob'
            });

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `survey_question_${questionIndex + 1}.${format}`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            toast.success(`Question ${questionIndex + 1} exported as ${format.toUpperCase()}!`);
        } catch (error) {
            console.error("Export failed", error);
            toast.error("Export failed");
        } finally {
            setExporting(false);
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '';
        return new Date(dateStr).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
        });
    };

    if (aggregatedData.length === 0) {
        return (
            <div className="text-center py-12">
                <BarChart3 className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-800 mb-2">No Data to Aggregate</h3>
                <p className="text-gray-500">Aggregated responses will appear here once employees complete the survey.</p>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            {aggregatedData.map((data, index) => (
                <div
                    key={data.question.id || index}
                    className="bg-white border border-gray-200 rounded-xl overflow-hidden hover:shadow-md transition-shadow"
                >
                    {/* Question Header */}
                    <div
                        className="p-5 cursor-pointer hover:bg-gray-50 transition-colors"
                        onClick={() => toggleQuestion(index)}
                    >
                        <div className="flex items-start justify-between gap-4">
                            <div className="flex items-start gap-4 flex-1">
                                <div className="flex-shrink-0 w-10 h-10 bg-indigo-100 text-indigo-600 rounded-lg flex items-center justify-center font-bold">
                                    {index + 1}
                                </div>
                                <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-2">
                                        <span className="px-2.5 py-0.5 bg-indigo-100 text-indigo-700 text-xs font-medium rounded-full">
                                            {data.question.question_type}
                                        </span>
                                        {data.question.is_required && (
                                            <span className="px-2.5 py-0.5 bg-red-100 text-red-700 text-xs font-medium rounded-full">
                                                Required
                                            </span>
                                        )}
                                    </div>
                                    <h4 className="text-gray-900 font-semibold text-lg mb-3">
                                        {data.question.question_text}
                                    </h4>
                                    
                                    {/* Stats */}
                                    <div className="flex flex-wrap items-center gap-4 text-sm">
                                        <div className="flex items-center gap-2">
                                            <User className="w-4 h-4 text-gray-400" />
                                            <span className="text-gray-600">
                                                <span className="font-semibold text-gray-900">{data.totalResponses}</span> responses
                                            </span>
                                        </div>
                                        {data.skippedCount > 0 && (
                                            <div className="flex items-center gap-2">
                                                <span className="text-gray-600">
                                                    <span className="font-semibold text-orange-600">{data.skippedCount}</span> skipped
                                                </span>
                                            </div>
                                        )}
                                        
                                        {/* Sentiment Distribution */}
                                        {(data.sentimentCounts.Positive > 0 || data.sentimentCounts.Negative > 0) && (
                                            <div className="flex items-center gap-2">
                                                <TrendingUp className="w-4 h-4 text-gray-400" />
                                                <div className="flex items-center gap-2">
                                                    {data.sentimentCounts.Positive > 0 && (
                                                        <span className="text-xs text-green-600 font-medium">
                                                            {data.sentimentCounts.Positive} Positive
                                                        </span>
                                                    )}
                                                    {data.sentimentCounts.Neutral > 0 && (
                                                        <span className="text-xs text-gray-600 font-medium">
                                                            {data.sentimentCounts.Neutral} Neutral
                                                        </span>
                                                    )}
                                                    {data.sentimentCounts.Negative > 0 && (
                                                        <span className="text-xs text-red-600 font-medium">
                                                            {data.sentimentCounts.Negative} Negative
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="flex items-center gap-2">
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleExportQuestion(index, 'csv');
                                    }}
                                    disabled={exporting}
                                    className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg text-xs font-medium hover:bg-gray-200 transition-colors disabled:opacity-50"
                                >
                                    <Download size={12} className="inline mr-1" />
                                    Export
                                </button>
                                {expandedQuestions.has(index) ? (
                                    <ChevronUp size={20} className="text-gray-400" />
                                ) : (
                                    <ChevronDown size={20} className="text-gray-400" />
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Responses List (Expanded) */}
                    {expandedQuestions.has(index) && (
                        <div className="border-t border-gray-200 bg-gray-50 p-5 space-y-3">
                            {data.responses.length > 0 ? (
                                data.responses.map((response, rIndex) => (
                                    <div
                                        key={rIndex}
                                        className="bg-white border border-gray-200 rounded-lg p-4"
                                    >
                                        <div className="flex items-start justify-between gap-4 mb-3">
                                            <div className="flex items-center gap-3">
                                                <div className="w-8 h-8 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-full flex items-center justify-center">
                                                    <User className="w-4 h-4 text-white" />
                                                </div>
                                                <div>
                                                    <p className="font-semibold text-gray-900 text-sm">
                                                        {response.employeeName}
                                                    </p>
                                                    <p className="text-xs text-gray-500">{response.employeeEmail}</p>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                {response.sentiment && (
                                                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                                        response.sentiment === 'Positive' ? 'bg-green-100 text-green-700' :
                                                        response.sentiment === 'Negative' ? 'bg-red-100 text-red-700' :
                                                        'bg-gray-100 text-gray-600'
                                                    }`}>
                                                        {response.sentiment}
                                                    </span>
                                                )}
                                                <span className="text-xs text-gray-500">
                                                    {formatDate(response.completedAt)}
                                                </span>
                                            </div>
                                        </div>
                                        <div className="pl-11">
                                            {response.skipped ? (
                                                <p className="text-gray-400 italic text-sm">Skipped this question</p>
                                            ) : (
                                                <p className="text-gray-800 text-sm leading-relaxed whitespace-pre-wrap">
                                                    {response.responseText || 'No response'}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <div className="text-center py-6 text-gray-500 text-sm">
                                    No responses for this question yet.
                                </div>
                            )}

                            {data.totalResponses > 3 && (
                                <div className="text-center pt-2">
                                    <p className="text-xs text-indigo-600 font-medium">
                                        Showing all {data.totalResponses} responses
                                    </p>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            ))}
        </div>
    );
};

export default AggregatedView;
